import {
  getResourceRoute,
  PUBLISHED_RESOURCE_ROUTES,
  type PublishedResource,
} from "./resource-library";
import {
  getProductUpdateRoute,
  PUBLISHED_PRODUCT_UPDATE_ROUTES,
  type PublishedProductUpdate,
} from "./product-updates";

export const SITE = {
  name: "PinkSpider",
  url: "https://pinkspider.ai",
  description:
    "PinkSpider is the AI-assisted marketing review and monitoring platform that helps brands review creator and marketing content, manage risk, and streamline remediation.",
  ogImage: "https://pinkspider.ai/og-image.png",
} as const;

export type Indexation = "index" | "noindex";

export interface PublicLink {
  href: string;
  label: string;
}

export interface PublicRouteDefinition {
  path: string;
  status: 200;
  indexation: Indexation;
  title: string;
  description: string;
  content: {
    h1: string;
    paragraphs: string[];
    links: PublicLink[];
  };
  resource?: PublishedResource;
  productUpdate?: PublishedProductUpdate;
  schemaType?: "WebPage" | "AboutPage" | "ContactPage" | "SoftwareApplication";
  changefreq?: "weekly" | "monthly";
  priority?: string;
  /**
   * FAQ text mirrored from the page's own FAQ section, kept here purely so
   * page-schema.ts can emit FAQPage JSON-LD without importing React page
   * components into shared code. Matches this file's existing pattern of a
   * separate SEO-facing `content` summary alongside the page's real JSX.
   */
  faqs?: { question: string; answer: string }[];
  /**
   * Absolute-path photo rendered above the fold as a CSS background-image
   * (not a plain <img>). Browsers don't discover background-image URLs
   * until the CSS is parsed and applied, well after the JS bundle loads, so
   * without a preload hint the box shows its background color for a beat.
   * Listing it here lets the server emit <link rel="preload" as="image">
   * so the fetch starts immediately alongside the rest of the document.
   */
  preloadHeroImage?: string;
}

const indexable = (
  definition: Omit<PublicRouteDefinition, "status" | "indexation">,
): PublicRouteDefinition => ({
  ...definition,
  status: 200,
  indexation: "index",
});

const noindex = (
  definition: Omit<PublicRouteDefinition, "status" | "indexation">,
): PublicRouteDefinition => ({
  ...definition,
  status: 200,
  indexation: "noindex",
});

/**
 * The public route contract is the source for route ownership, crawlable copy,
 * metadata, sitemap eligibility, and the browser's route metadata.
 *
 * The initial content is intentionally concise: it contains the page's actual
 * H1, lead copy, and high-value internal links while the React page loads.
 */
export const PUBLIC_ROUTES = {
  "/": indexable({
    path: "/",
    title: "PinkSpider | Move Marketing Faster, Catch Risk Earlier",
    description:
      "Review influencer and marketing content before it goes live, monitor affiliate content after publication, and streamline approvals with PinkSpider.",
    schemaType: "WebPage",
    changefreq: "weekly",
    priority: "1.0",
    content: {
      h1: "Move marketing faster. Catch risk earlier.",
      paragraphs: [
        "PinkSpider helps teams review influencer and brand-owned content before it goes live, monitor affiliate content after it publishes, and turn potential issues into clear next steps.",
        "Review, monitor, resolve, train, and understand the work between a creator brief and a live post.",
      ],
      links: [
        { href: "/platform", label: "See the platform" },
        { href: "/solutions/marketing-review", label: "Explore marketing review" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/platform": indexable({
    path: "/platform",
    title: "AI Content Operating System for Brands & Creators | PinkSpider",
    description: "One workspace that automates content review before publication, monitoring after it, and remediation, training, and reporting in between.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.9",
    content: {
      h1: "The AI-native content operating system between brands and creators.",
      paragraphs: ["PinkSpider is where content gets briefed, reviewed, approved, monitored, and improved, all on one shared brand knowledge base.", "Keep your team aligned without turning compliance into a stop sign."],
      links: [{ href: "/influencer-review", label: "Review before publication" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "What parts of the marketing workflow can PinkSpider support?", answer: "PinkSpider connects pre-publication review, post-publication monitoring, creator follow-up, training, and program insight in one working context." },
      { question: "Does PinkSpider replace human review?", answer: "PinkSpider automates the vast majority of routine review, so your team can focus its judgment on the decisions that actually need it. People still make and document the final call." },
    ],
  }),
  "/influencer-review": indexable({
    path: "/influencer-review",
    title: "Influencer Content Review & Compliance Software | PinkSpider",
    description: "Automatically check influencer scripts, captions, frames, and disclosures before publication with consistent, brand-aware guidance.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.8",
    preloadHeroImage: "/influencer-review-thumb.jpg",
    content: {
      h1: "Compliant creator content, approved in minutes, not days.",
      paragraphs: ["Influencer Review automatically checks paid creator content against your brand and category guidance before it goes live, grounding every flag in the submitted caption, transcript, or frame.", "Creators get useful revision guidance instead of a vague rejection, while reviewers keep the final say."],
      links: [{ href: "/platform", label: "Explore the platform" }, { href: "/academy", label: "Turn feedback into training" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "Can it review video content?", answer: "Yes. Influencer Review works with the caption, transcript, frames, and visual context, so a flag points to a specific piece of the submission." },
      { question: "Can teams apply their own guidance?", answer: "Yes. Brand-specific rules and approved claims are reviewed alongside the content, not a separate document." },
      { question: "Does it make the final approval?", answer: "It automates the vast majority of routine checks and only surfaces what needs a second look, so your team can focus on the decisions that matter and keep final say." },
      { question: "What if content needs a change?", answer: "The creator gets revision guidance tied to the flag, not a generic rejection." },
    ],
  }),
  "/affiliate-monitoring": indexable({
    path: "/affiliate-monitoring",
    title: "Affiliate Monitoring & Compliance Software | PinkSpider",
    description: "Automatically monitor affiliate and creator content after publication for disclosures, claims, brand alignment, and risk signals.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.8",
    preloadHeroImage: "/affiliate-influencer-thumb.jpg",
    content: {
      h1: "Automatically catch what your review queue never saw.",
      paragraphs: ["Affiliate Monitoring automatically discovers and reviews creator and affiliate content once it's already live, surfacing evidence-backed signals so your team only reviews what needs a second look.", "Use risk signals to prioritize outreach, document decisions, and keep the relationship constructive."],
      links: [{ href: "/platform", label: "See the full platform" }, { href: "/solutions/marketing-teams", label: "For marketing teams" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "What is affiliate monitoring?", answer: "It's finding published affiliate content, preserving evidence, prioritizing by risk, and routing follow-up to a person." },
      { question: "Does this make a legal determination?", answer: "No. Monitoring surfaces content and evidence for review. Your team decides what action is appropriate." },
      { question: "What if a post is edited or deleted?", answer: "Evidence is captured when a signal is detected, so it stays available even if the post changes." },
      { question: "Can it replace pre-publication review?", answer: "No, it complements it. Influencer Review covers content submitted before it goes live; this covers what publishes without that step." },
    ],
  }),
  "/solutions/marketing-review": indexable({
    path: "/solutions/marketing-review",
    title: "AI Marketing Review Software & Compliance Workflow | PinkSpider",
    description: "Automate the shared marketing review workflow for brand, compliance, regulatory, and creator teams so campaigns move forward with context.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Your claims matrix and brand book, enforced on the same asset.",
      paragraphs: ["Upload the policies, substantiation, and guidelines you already have. PinkSpider compiles them into rules and checks every piece of marketing against both, with a citation back to the document, page, and text.", "Set the review standard once, then give each team a clear role in the decision."],
      links: [{ href: "/influencer-review", label: "Review before publish" }, { href: "/solutions/compliance-regulatory", label: "For compliance and regulatory" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "Does this only cover influencers?", answer: "No. Marketing Review covers brand-owned marketing, ad creative, email, and collateral, alongside influencer reviews." },
      { question: "Who can join a review?", answer: "Marketing, brand, compliance, and regulatory can all use the same review context while roles stay clear." },
      { question: "How does this reduce team handoffs?", answer: "The submitted asset, guidance, evidence, and comments stay attached to one review, not split across threads." },
      { question: "Does it make the compliance decision?", answer: "No. It organizes evidence and guidance so the right people can decide, the final call is always a human's." },
    ],
  }),
  "/academy": indexable({
    path: "/academy",
    title: "Academy: Creator & Team Training | PinkSpider",
    description: "Turn automated content flags into practical lessons that help creators and internal teams understand disclosures, claims, and brand rules.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Turn automated flags into lessons for creators and teams.",
      paragraphs: ["Academy turns automated review flags into clear, reusable lessons, for the creators posting content and the internal teams reviewing it. Teach the reason behind a rule, not just the red mark.", "Keep guidance close to the work so education becomes part of both the creative process and your team's review practice."],
      links: [{ href: "/influencer-review", label: "See influencer review" }, { href: "/solutions/creator-marketing", label: "For creator marketing" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "How does Academy connect to review?", answer: "Recurring flags from Influencer Review and Affiliate Monitoring can become a focused lesson explaining the issue and why." },
      { question: "Can teams see lesson completion?", answer: "Yes. Completion status is visible within the workflow, so your team knows if follow-up is needed." },
      { question: "Will this feel like compliance training?", answer: "Lessons are short and specific to the person's own work, the goal is a quick explanation, not a lecture." },
      { question: "Is Academy just for creators?", answer: "No. The same explanations used for creators also give internal reviewers and coaches a focused way to understand why a guideline exists." },
    ],
  }),
  "/platform/analytics": indexable({
    path: "/platform/analytics",
    title: "Marketing Compliance Analytics & Insights | PinkSpider",
    description: "Understand automated review volume, recurring flags, risk themes, and program patterns with creator marketing analytics from PinkSpider.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "See where automated review is saving you time.",
      paragraphs: ["Analytics connects individual flags to the questions leaders ask: where work is getting stuck, which guidance repeats, and what deserves a closer look.", "Use trends to focus enablement and make thoughtful decisions about your creator program."],
      links: [{ href: "/platform", label: "Explore the platform" }, { href: "/solutions/marketing-teams", label: "For marketing teams" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "What can marketing teams learn from review analytics?", answer: "Teams can inspect recurring themes, review workload, creator patterns, and trends that may point to a better brief or more useful training." },
      { question: "Are analytics a compliance guarantee?", answer: "No. Analytics summarize program activity and signals; they do not guarantee an outcome or replace a qualified reviewer." },
      { question: "What does Ask Analytics actually do?", answer: "It uses AI to turn a plain-language question into a summary of your program's own review data. Treat it as a fast starting point, not a verified determination." },
      { question: "Can I see which creators are driving a recurring issue?", answer: "Yes. Segment analysis can slice by creator, and a recurring pattern can be connected directly to an Academy lesson or a brief update." },
    ],
  }),
  "/solutions/marketing-teams": indexable({
    path: "/solutions/marketing-teams",
    title: "Marketing Campaign Compliance Workflow | PinkSpider",
    description: "Keep creator campaigns moving with one visible workflow for review, approvals, feedback, and escalation. PinkSpider automates routine checks while marketing teams stay in control.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Create boldly. Launch confidently.",
      paragraphs: ["PinkSpider reviews your marketing content, from Figma and Claude Design files to emails, paid social, and creator videos, inside the tools your team already uses, so great ideas move from brief to live with less back-and-forth.", "Bring brand, compliance, and campaign owners into the same conversation, so launches keep their momentum and creators get feedback they can actually use."],
      links: [{ href: "/solutions/marketing-review", label: "See the review workflow" }, { href: "/platform/analytics", label: "Understand program patterns" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "What content can PinkSpider review?", answer: "PinkSpider can review the content your team creates, including Figma designs, emails, paid social, website copy, and influencer and affiliate videos before and after they post." },
      { question: "Does PinkSpider work with our project management tools?", answer: "Yes. PinkSpider integrates with Asana and Monday, and the setup is flexible, so review tasks and approvals fit the way your team already plans campaigns." },
      { question: "How does PinkSpider learn our brand standards?", answer: "Your team uploads brand guidelines, approved claims, and supporting documents. PinkSpider uses them in each review and continues learning as your compliance team approves or edits its recommendations." },
      { question: "Can our regulatory and legal teams still approve content?", answer: "Yes. PinkSpider handles routine checks and routes the judgment calls to your regulatory or legal reviewers, so experts stay involved where it matters most." },
    ],
  }),
  "/solutions/compliance-regulatory": indexable({
    path: "/solutions/compliance-regulatory",
    title: "Marketing Compliance Software for Compliance & Regulatory Teams | PinkSpider",
    description: "Stop repeating the same feedback and fielding last-minute emergency requests. PinkSpider automates the repetitive review work and gently teaches marketing teams your company's risk tolerance.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Finally, a review queue that isn't always on fire.",
      paragraphs: [
        "You’ve explained the same flagged claim more times than you can count, and it still shows up as another urgent review request that pulls you out of your work. PinkSpider automates the review work that’s been eating your day, so what actually reaches you is the judgment call, not the fire drill.",
        "Save your expertise for the decisions only you can make.",
      ],
      links: [{ href: "/solutions/marketing-review", label: "Share one review workflow" }, { href: "/trust", label: "See our trust approach" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "Does PinkSpider provide legal advice?", answer: "No. PinkSpider supports review with organized evidence and guidance while qualified people remain responsible for legal decisions." },
      { question: "How can compliance teams avoid becoming the bottleneck?", answer: "They can help define a consistent review standard and focus their attention on content and questions that require their judgment." },
    ],
  }),
  "/solutions/creator-marketing": indexable({
    path: "/solutions/creator-marketing",
    title: "Creator Marketing Review & Monitoring | PinkSpider",
    description: "Give creators clear, respectful feedback before publish and keep visibility into affiliate and creator content after publish with PinkSpider.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Keep the voice. Get the claim right.",
      paragraphs: ["PinkSpider reviews influencer and affiliate videos before they post and monitors content after it goes live, so your team can give creators friendly, useful feedback and protect the partnerships you've worked hard to build.", "Turn repeated flags into better briefs and enablement, so every campaign gets a little easier to run."],
      links: [{ href: "/academy", label: "Train creators in context" }, { href: "/affiliate-monitoring", label: "Monitor affiliate content" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "Can PinkSpider review influencer videos before they post?", answer: "Yes. PinkSpider reviews video, captions, and transcripts before content goes live, then shares clear suggested edits your team can send to creators." },
      { question: "Does PinkSpider monitor affiliate content after it's published?", answer: "Yes. PinkSpider monitors live creator and affiliate posts on Instagram, TikTok, TikTok Shop, and YouTube, flags content that needs attention, and gives your team a simple path to follow up." },
      { question: "Can PinkSpider find affiliates who aren't in our program?", answer: "Yes. PinkSpider also monitors content from affiliates who were never formally onboarded, such as TikTok Shop affiliates, so your team can spot compliance issues and reach out early." },
      { question: "How does PinkSpider keep feedback creator-friendly?", answer: "Instead of a vague rejection, creators get one specific note with a suggested edit that keeps their personal experience front and center." },
    ],
  }),
  "/solutions/agencies": indexable({
    path: "/solutions/agencies",
    title: "Creator Compliance Workflow for Agencies | PinkSpider",
    description: "Protect agency margin and keep every client's playbook distinct with clear creator-content review, evidence-backed recommendations, and one repeatable workflow.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Grow your roster, not your review queue.",
      paragraphs: ["PinkSpider gives agencies one review workflow for client marketing content, from Figma and Claude Design files to emails and influencer and affiliate videos, with each brand's guidelines kept separate and approvals easy to track.", "Protect your margin, keep account teams moving, and grow the portfolio without multiplying the chaos behind every launch."],
      links: [{ href: "/platform", label: "See the platform" }, { href: "/solutions/marketing-review", label: "Build a shared workflow" }, { href: "/demo", label: "Request a demo" }],
    },
    faqs: [
      { question: "Can we manage multiple client brands in one account?", answer: "Yes. Each client has its own playbook, claims, and approvers, so guidance stays separate while your team works from one platform." },
      { question: "How does PinkSpider learn each client's standards?", answer: "Your team uploads each brand's guidelines, approved claims, and supporting documents. PinkSpider uses them in each review and keeps learning as your team approves or edits its recommendations." },
      { question: "How do we explain review decisions to clients?", answer: "PinkSpider keeps the change, the reasoning, and the supporting evidence together, so your team is ready for the client conversation." },
      { question: "What types of client content can we review?", answer: "Figma and Claude Design files, emails, paid social, website copy, and influencer and affiliate videos before and after they post, including content from affiliates who aren't formally onboarded." },
    ],
  }),
  "/industries": indexable({
    path: "/industries",
    title: "Industry-Specific Marketing Compliance Software | PinkSpider",
    description: "See how PinkSpider adapts claim, disclosure, and brand-safety review for health, beauty, finance, alcohol, gaming, and cannabis marketing.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Review marketing with the context your category requires.",
      paragraphs: [
        "Claims, disclosures, platform rules, and brand standards vary by category. PinkSpider ships with category-specific review considerations so your team isn't applying one generic standard to every kind of content.",
        "Choose an industry to see the content signals, evidence examples, and workflow that fit your category.",
      ],
      links: [
        { href: "/industries/health-wellness", label: "Health, Supplements & Wellness" },
        { href: "/industries/beauty-cosmetics", label: "Beauty & Cosmetics" },
        { href: "/industries/finance-fintech", label: "Finance & Fintech" },
        { href: "/industries/alcohol-spirits", label: "Alcohol & Spirits" },
        { href: "/industries/gambling-gaming", label: "Gambling & Gaming" },
        { href: "/industries/cannabis-cbd", label: "Cannabis & CBD" },
        { href: "/industries/fitness-activewear", label: "Fitness & Activewear" },
        { href: "/industries/apparel-fashion", label: "Apparel & Fashion" },
        { href: "/industries/food-beverage", label: "Food & Beverage" },
        { href: "/industries/retail-cpg", label: "Retail & CPG" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/industries/health-wellness": indexable({
    path: "/industries/health-wellness",
    title: "Health & Supplement Marketing Compliance Software | PinkSpider",
    description: "Review health, supplement, and wellness creator content for claims, disclosures, and brand rules before it reaches your audience.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "More confidence for marketing where claims matter.",
      paragraphs: ["PinkSpider helps health and wellness brands review creator language in context, flag potential claim and disclosure issues, and give partners a clear path to revise.", "Use your own guidelines alongside applicable rules, with a human team making the final call."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/solutions/compliance-regulatory", label: "Support compliance review" }, { href: "/academy", label: "Turn feedback into training" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/beauty-cosmetics": indexable({
    path: "/industries/beauty-cosmetics",
    title: "Beauty & Cosmetics Marketing Compliance Software | PinkSpider",
    description: "Review beauty and cosmetics creator content for drug-claim language, before/after edits, and disclosures before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Beauty content that stays true to what it can prove.",
      paragraphs: ["PinkSpider helps beauty and cosmetics brands check creator language and before/after edits against category considerations before content goes live.", "Give creators specific revision guidance while your team keeps the final call on every claim."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/finance-fintech": indexable({
    path: "/industries/finance-fintech",
    title: "Finance & Fintech Marketing Compliance Software | PinkSpider",
    description: "Review investment, lending, and crypto creator content for risk disclosures and material-connection language before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Give financial content the scrutiny investors expect.",
      paragraphs: ["PinkSpider helps finance and fintech brands review creator content for return claims, risk disclosures, and material-connection language before it goes live.", "Keep evidence and guidance together so your team can move quickly without skipping a required disclosure."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/solutions/compliance-regulatory", label: "Support compliance review" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/alcohol-spirits": indexable({
    path: "/industries/alcohol-spirits",
    title: "Alcohol & Spirits Marketing Compliance Software | PinkSpider",
    description: "Review alcohol and spirits creator content for responsible-consumption messaging and disclosures before it reaches your audience.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Marketing that celebrates the moment, responsibly.",
      paragraphs: ["PinkSpider helps alcohol and spirits brands check creator scripts and captions for responsible-consumption context before content publishes.", "Keep brand and label guidance close to the review so every partner post reflects the same standard."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/academy", label: "Turn feedback into training" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/gambling-gaming": indexable({
    path: "/industries/gambling-gaming",
    title: "Gambling & Gaming Marketing Compliance Software | PinkSpider",
    description: "Review gaming, sports betting, and casino creator content for responsible-play messaging and odds claims before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Play up the excitement without overpromising the odds.",
      paragraphs: ["PinkSpider helps gambling and gaming brands review creator content for winnings claims, responsible-play messaging, and disclosures before it goes live.", "Support a big roster of creators and affiliates with one consistent review standard."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/cannabis-cbd": indexable({
    path: "/industries/cannabis-cbd",
    title: "Cannabis & CBD Marketing Compliance Software | PinkSpider",
    description: "Review cannabis and CBD creator content for medical claims and state-specific legality statements before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Navigate cannabis marketing state by state, with confidence.",
      paragraphs: ["PinkSpider helps cannabis and CBD brands check creator claims and legality statements against category considerations that shift by state.", "Give partners clear revision guidance while your team keeps the final call."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/solutions/compliance-regulatory", label: "Support compliance review" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/fitness-activewear": indexable({
    path: "/industries/fitness-activewear",
    title: "Fitness & Activewear Marketing Compliance Software | PinkSpider",
    description: "Review fitness, activewear, and program creator content for transformation claims and disclosures before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Transformation content that stays honest about the work.",
      paragraphs: ["PinkSpider helps fitness and activewear brands check creator transformation claims and program disclosures before content goes live.", "Give ambassadors and affiliates clear revision guidance while your team keeps the final call."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/apparel-fashion": indexable({
    path: "/industries/apparel-fashion",
    title: "Apparel & Fashion Marketing Compliance Software | PinkSpider",
    description: "Review fashion haul, discount-code, and sustainability creator content for disclosures and claims before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Keep every haul, drop, and discount code accountable.",
      paragraphs: ["PinkSpider helps apparel and fashion brands check creator hauls, discount codes, and sustainability language against category considerations before content goes live.", "Keep evidence and guidance together so partners get a clear, consistent standard."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/food-beverage": indexable({
    path: "/industries/food-beverage",
    title: "Food & Beverage Marketing Compliance Software | PinkSpider",
    description: "Review food and beverage creator content for nutrition, ingredient, and comparative claims before it publishes.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Marketing that stays appetizing and accurate.",
      paragraphs: ["PinkSpider helps food and beverage brands review creator captions and scripts for nutrition, health, and comparative claims before content goes live.", "Keep evidence and guidance together so partners get a clear, consistent standard on every recipe, launch, and promotion."],
      links: [{ href: "/influencer-review", label: "Review content before publish" }, { href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/industries/retail-cpg": indexable({
    path: "/industries/retail-cpg",
    title: "Retail & CPG Marketing Compliance Software | PinkSpider",
    description: "Review high-volume creator and affiliate content for disclosure, pricing, and guideline consistency across retail and CPG programs.",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "Keep campaign guidelines consistent at creator scale.",
      paragraphs: ["PinkSpider helps retail and CPG brands check disclosure, pricing, and guideline consistency across large creator and affiliate rosters, including commerce-enabled posts.", "Catch drift before it multiplies across a roster, and find it fast after publication if it does."],
      links: [{ href: "/affiliate-monitoring", label: "Monitor live content" }, { href: "/influencer-review", label: "Review content before publish" }, { href: "/demo", label: "Request a demo" }],
    },
  }),
  "/trust": indexable({
    path: "/trust",
    title: "Trust Center | PinkSpider",
    description: "Learn how PinkSpider approaches responsible AI assistance, data protection, access controls, and transparent product boundaries.",
    changefreq: "monthly",
    priority: "0.7",
    content: {
      h1: "Trust is part of the product.",
      paragraphs: ["PinkSpider is designed to support accountable marketing decisions, not replace them. We are clear about what the platform does, where human judgment belongs, and how we approach data protection.", "Read the overview, then contact us with questions about your team’s requirements."],
      links: [{ href: "/privacy", label: "Read the privacy policy" }, { href: "/terms", label: "Read the terms" }, { href: "/contact", label: "Contact the team" }],
    },
  }),
  "/demo": indexable({
    path: "/demo",
    title: "Request a PinkSpider Demo | Creator Marketing Compliance",
    description: "See how PinkSpider helps teams review, monitor, resolve, train, and understand creator marketing content in one workspace.",
    schemaType: "ContactPage",
    changefreq: "monthly",
    priority: "0.9",
    content: {
      h1: "See PinkSpider on content your team actually reviews.",
      paragraphs: ["Bring a few creator posts, drafts, or examples from your current workflow and we’ll build the walkthrough around them, alongside a conversation about review, monitoring, remediation, training, and analytics.", "No pressure, no generic pitch. A real person will follow up with the next useful step."],
      links: [{ href: "/platform", label: "Explore the platform first" }, { href: "/trust", label: "Read our trust approach" }],
    },
  }),
  "/about": indexable({
    path: "/about",
    title: "About PinkSpider | AI Influencer Compliance Company",
    description:
      "We're building the compliance layer for creator marketing - making influencer programs safe, fast, and trustworthy for an entire industry.",
    schemaType: "AboutPage",
    changefreq: "monthly",
    priority: "0.7",
    content: {
      h1: "The safety net for creator marketing, built to be invisible.",
      paragraphs: [
        "We help marketing teams catch compliance risk across creator content, campaigns, and everyday marketing materials, without losing sleep over it. The best safety net is the one nobody notices until it catches something.",
        "PinkSpider exists to clear the review bottleneck so teams can give creators useful guidance and keep campaigns moving.",
      ],
      links: [
        { href: "/platform", label: "See how PinkSpider works" },
        { href: "/careers", label: "Join the team" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/contact": indexable({
    path: "/contact",
    title: "Contact PinkSpider | Creator Marketing Compliance",
    description:
      "Get in touch with the PinkSpider team. Real humans answer questions about product demos, support, security, and partnerships.",
    schemaType: "ContactPage",
    changefreq: "monthly",
    priority: "0.6",
    content: {
      h1: "Let's talk.",
      paragraphs: [
        "No ticket queue, no chatbot. Pick the right inbox and a human on our team will get back to you within two business days.",
      ],
      links: [
        { href: "/demo", label: "Request a demo" },
        { href: "/platform", label: "Learn about the platform" },
        { href: "/trust", label: "Read about trust and security" },
      ],
    },
  }),
  "/careers": indexable({
    path: "/careers",
    title: "Careers at PinkSpider | Join the Compliance AI Team",
    description:
      "Help us make creator marketing safe. Meet the PinkSpider team building the AI compliance layer for influencer marketing.",
    schemaType: "WebPage",
    changefreq: "weekly",
    priority: "0.6",
    content: {
      h1: "Help us make creator marketing safe.",
      paragraphs: [
        "We are a small team building the compliance layer for an entire industry. The early people shape everything that comes after.",
        "We are always open to the right people in AI, compliance, and product.",
      ],
      links: [
        { href: "/contact", label: "Get in touch" },
        { href: "/about", label: "Learn about PinkSpider" },
      ],
    },
  }),
  "/resources": indexable({
    path: "/resources",
    title: "Resources | Creator Marketing Compliance Guides | PinkSpider",
    description:
      "Source-backed guides for brands building practical influencer, FTC disclosure, and affiliate compliance programs.",
    schemaType: "WebPage",
    changefreq: "weekly",
    priority: "0.8",
    content: {
      h1: "Practical resources for marketing and creator compliance.",
      paragraphs: [
        "Source-backed guides for brands that want a clearer way to review creator content and brand-owned marketing, monitor affiliate programs after launch, and train partners before problems reach a live post.",
        "Start with a cornerstone guide, or browse by the product area your team runs: influencer review, marketing review, affiliate monitoring, or Academy training.",
      ],
      links: [
        {
          href: "/resources/creator-marketing-compliance-guide",
          label: "The Creator Marketing Compliance Guide",
        },
        {
          href: "/resources/escalation-playbook",
          label: "Escalation Playbook: Process for Non-Compliant Live Posts",
        },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/product-updates": indexable({
    path: "/product-updates",
    title: "Product Updates | PinkSpider",
    description:
      "See what's new in the PinkSpider product: features, integrations, and improvements to review, monitoring, and compliance workflows.",
    schemaType: "WebPage",
    changefreq: "weekly",
    priority: "0.6",
    content: {
      h1: "What's new at PinkSpider.",
      paragraphs: [
        "Features, integrations, and improvements as they ship, in one place.",
      ],
      links: [
        { href: "/product-updates/figma-plugin", label: "The PinkSpider Figma Plugin" },
        { href: "/platform", label: "Explore the platform" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/platform/integrations": indexable({
    path: "/platform/integrations",
    title: "Marketing Platform Integrations | PinkSpider",
    description:
      "See the approved PinkSpider integrations for creator platforms, workflow collaboration, and data connections.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.7",
    content: {
      h1: "Connect the tools your marketing team already uses.",
      paragraphs: [
        "PinkSpider brings approved creator, commerce, collaboration, and workspace connections into a clearer review workflow.",
        "Talk to us about the workflow you want to connect next.",
      ],
      links: [
        { href: "/platform", label: "Explore the platform" },
        { href: "/solutions/marketing-teams", label: "See the marketing team workflow" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/platform/compliance-intelligence": indexable({
    path: "/platform/compliance-intelligence",
    title: "Compliance Intelligence: A Brand-Specific Review Standard | PinkSpider",
    description: "See how PinkSpider turns brand documents, playbooks, a knowledge library, and reviewer history into one evidence-backed review standard.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.8",
    content: {
      h1: "One review standard, built from everything your team already knows.",
      paragraphs: [
        "Compliance Intelligence turns your brand documents, compliance playbooks, knowledge library, and reviewer history into a single standard that every review applies, instead of a generic AI checklist.",
      ],
      links: [
        { href: "/influencer-review", label: "See it applied before publication" },
        { href: "/affiliate-monitoring", label: "See it applied after publication" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
    faqs: [
      { question: "Does Compliance Intelligence make the final call?", answer: "No. It surfaces flags, retrieved rules, and evidence for a reviewer. Publishing a proposed learning, and every review decision, stays with a person." },
      { question: "Where do the rules come from?", answer: "From your own brand documents, compliance playbooks, and knowledge library, plus category guidance PinkSpider maintains, always cited to its source." },
      { question: "How does reviewer intelligence learn?", answer: "It looks for patterns in how your reviewers have actually decided, then proposes a specific rule. It stays inactive until a person edits, tests, or publishes it." },
      { question: "Can this guarantee compliance?", answer: "No tool can guarantee compliance. PinkSpider is built to make the evidence behind a decision clear and consistent, not to replace legal or compliance judgment." },
    ],
  }),
  "/platform/automations": indexable({
    path: "/platform/automations",
    title: "Automations | PinkSpider",
    description: "Automations turns review signals into repeatable next steps.",
    schemaType: "SoftwareApplication",
    changefreq: "monthly",
    priority: "0.6",
    content: {
      h1: "Turn review signals into repeatable next steps.",
      paragraphs: [
        "Automations connects what your team already reviews and monitors to the follow-up work that usually happens by hand, so a recurring flag can lead straight to the right next step.",
        "Every automation still ends with a person deciding, not the system acting alone.",
      ],
      links: [
        { href: "/platform", label: "Explore the platform" },
        { href: "/platform/analytics", label: "See program analytics" },
        { href: "/demo", label: "Request a demo" },
      ],
    },
  }),
  "/privacy": indexable({
    path: "/privacy",
    title: "Privacy Policy | PinkSpider",
    description:
      "How PinkSpider collects, uses, and protects your information across our AI-powered influencer compliance platform.",
    schemaType: "WebPage",
    changefreq: "monthly",
    priority: "0.5",
    content: {
      h1: "Privacy Policy",
      paragraphs: [
        'PinkSpider, LLC ("PinkSpider," "we," "us," or "our") provides an AI-powered influencer compliance platform. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our Service.',
      ],
      links: [
        { href: "/terms", label: "Read the Terms of Service" },
        { href: "/trust", label: "Read about trust and security" },
        { href: "/contact", label: "Contact PinkSpider" },
      ],
    },
  }),
  "/terms": indexable({
    path: "/terms",
    title: "Terms of Service | PinkSpider",
    description:
      "The terms that govern your use of PinkSpider's AI-powered influencer compliance platform.",
    schemaType: "WebPage",
    changefreq: "monthly",
    priority: "0.5",
    content: {
      h1: "Terms of Service",
      paragraphs: [
        "Welcome to PinkSpider, an AI-powered influencer compliance platform designed to help brands and their influencers streamline content review for regulatory and brand guideline compliance.",
      ],
      links: [
        { href: "/privacy", label: "Read the Privacy Policy" },
        { href: "/contact", label: "Contact PinkSpider" },
      ],
    },
  }),
  "/unsubscribe": noindex({
    path: "/unsubscribe",
    title: "Unsubscribe | PinkSpider",
    description: "Opt out of PinkSpider emails.",
    content: {
      h1: "Unsubscribe",
      paragraphs: [
        "Enter your email below and we'll remove you from PinkSpider marketing communications.",
      ],
      links: [{ href: "/", label: "Back to PinkSpider" }],
    },
  }),
} satisfies Record<string, PublicRouteDefinition>;

/**
 * Collision-safe public URLs from the approved SEO brief. They are reserved
 * for future public pages; until those pages exist, they must not be treated
 * as indexable routes or leak into the sitemap.
 */
export const RESERVED_PUBLIC_PATHS = [
] as const;

export const LEGACY_PUBLIC_REDIRECTS = {
  "/product": "/platform",
  "/solutions": "/solutions/marketing-review",
  "/security": "/trust",
  "/compliance-guide": "/resources",
  "/creator-training": "/academy",
  "/industries/gaming-igaming": "/industries/gambling-gaming",
} as const;

export const NON_INDEXABLE_PATHS = [
  "/login",
  "/reset-password",
  "/invite",
  "/setup",
  "/share",
  "/preview",
  "/oauth",
  "/marketing-review",
  "/analytics",
  "/automations",
  "/integrations",
  "/app",
] as const;

export const PUBLIC_NAV_LINKS: PublicLink[] = [
  { href: "/platform", label: "Platform" },
  { href: "/influencer-review", label: "Products" },
  { href: "/solutions/marketing-review", label: "Solutions" },
  { href: "/industries", label: "Industries" },
  { href: "/resources", label: "Resources" },
  { href: "/about", label: "Company" },
  { href: "/demo", label: "Request a Demo" },
];

export const INDEXABLE_PUBLIC_ROUTES = Object.values(PUBLIC_ROUTES).filter(
  (route) => route.indexation === "index",
);

export const INDEXABLE_RESOURCE_ROUTES = PUBLISHED_RESOURCE_ROUTES;

export const INDEXABLE_PRODUCT_UPDATE_ROUTES = PUBLISHED_PRODUCT_UPDATE_ROUTES;

export const ALL_INDEXABLE_PUBLIC_ROUTES = [
  ...INDEXABLE_PUBLIC_ROUTES,
  ...INDEXABLE_RESOURCE_ROUTES,
  ...INDEXABLE_PRODUCT_UPDATE_ROUTES,
];

export const NOINDEX_PUBLIC_ROUTES = Object.values(PUBLIC_ROUTES).filter(
  (route) => route.indexation === "noindex",
);

export function getPublicRoute(path: string): PublicRouteDefinition | undefined {
  return (
    PUBLIC_ROUTES[path as keyof typeof PUBLIC_ROUTES] ??
    getResourceRoute(path) ??
    getProductUpdateRoute(path)
  );
}

export function isExplicitlyNoIndexPath(path: string): boolean {
  return (
    NON_INDEXABLE_PATHS.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    ) || RESERVED_PUBLIC_PATHS.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  );
}
